import React from 'react';

/**
 * StiffnessSection Component
 * Range of Motion and stiffness assessment
 * Adapts movements based on injury type (shoulder, ankle, lumbar)
 */
const StiffnessSection = ({ config, formData, onChange }) => {
  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const handleCheckboxChange = (fieldId, option) => {
    const currentValues = formData[fieldId] || [];
    const newValues = currentValues.includes(option)
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];
    onChange(fieldId, newValues);
  };

  const renderMovementAssessment = (movement) => {
    const restrictionKey = `${movement.id}_restriction`;
    const painKey = `${movement.id}_pain`;
    
    return (
      <div key={movement.id} className="movement-item">
        <div className="movement-header">
          <span className="movement-icon">{movement.icon}</span>
          <span className="movement-label">{movement.label}</span>
        </div>
        
        <div className="movement-assessment">
          <div className="assessment-field">
            <label className="mini-label">Restriction:</label>
            <select
              value={formData[restrictionKey] || ''}
              onChange={(e) => handleFieldChange(restrictionKey, e.target.value)}
              className="mini-select"
            >
              <option value="">Select...</option>
              <option value="None">None</option>
              <option value="Mild">Mild</option>
              <option value="Moderate">Moderate</option>
              <option value="Severe">Severe</option>
              <option value="Unable to perform">Unable to perform</option>
            </select>
          </div>
          
          <div className="assessment-field">
            <label className="mini-label">Pain during movement:</label>
            <div className="mini-radio-group">
              <label className="mini-radio">
                <input
                  type="radio"
                  name={painKey}
                  value="Yes"
                  checked={formData[painKey] === 'Yes'}
                  onChange={(e) => handleFieldChange(painKey, e.target.value)}
                />
                <span>Yes</span>
              </label>
              <label className="mini-radio">
                <input
                  type="radio"
                  name={painKey}
                  value="No"
                  checked={formData[painKey] === 'No'}
                  onChange={(e) => handleFieldChange(painKey, e.target.value)}
                />
                <span>No</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="stiffness-section">
      <style jsx>{`
        .stiffness-section {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 24px;
          margin: 24px 0;
        }

        .section-intro {
          background: white;
          padding: 14px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #3b82f6;
          font-size: 14px;
          color: #1e40af;
        }

        .movements-container {
          display: grid;
          gap: 16px;
        }

        .movement-item {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          padding: 16px;
          transition: border-color 0.2s;
        }

        .movement-item:hover {
          border-color: #3b82f6;
        }

        .movement-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }

        .movement-icon {
          font-size: 24px;
          width: 32px;
          text-align: center;
        }

        .movement-label {
          font-weight: 600;
          color: #1f2937;
          font-size: 15px;
          flex: 1;
        }

        .movement-assessment {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .assessment-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mini-label {
          font-size: 13px;
          color: #6b7280;
          font-weight: 500;
        }

        .mini-select {
          padding: 8px 10px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
          background: white;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .mini-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .mini-radio-group {
          display: flex;
          gap: 16px;
        }

        .mini-radio {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }

        .mini-radio input[type="radio"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .additional-questions {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 2px solid #e5e7eb;
        }

        .additional-questions h4 {
          font-size: 16px;
          color: #1f2937;
          margin: 0 0 16px 0;
          font-weight: 600;
        }

        .field-group {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
        }

        .field-label {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
        }

        .radio-group {
          display: flex;
          gap: 24px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .radio-option:hover {
          background: #f9fafb;
        }

        .radio-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .checkbox-option:hover {
          background: #f9fafb;
        }

        .checkbox-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .movement-assessment {
            grid-template-columns: 1fr;
          }

          .stiffness-section {
            padding: 16px;
          }
        }
      `}</style>

      <div className="section-intro">
        📏 Please assess each movement below. Compare with your unaffected side if applicable.
      </div>

      <div className="movements-container">
        {config.movements && config.movements.map(movement => 
          renderMovementAssessment(movement)
        )}
      </div>

      {config.morningStiffness && (
        <div className="additional-questions">
          <h4>Additional Assessment</h4>
          
          <div className="field-group">
            <label className="field-label">
              Do you experience morning stiffness?
            </label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="morningStiffness"
                  value="Yes"
                  checked={formData.morningStiffness === 'Yes'}
                  onChange={(e) => handleFieldChange('morningStiffness', e.target.value)}
                />
                <span>Yes</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="morningStiffness"
                  value="No"
                  checked={formData.morningStiffness === 'No'}
                  onChange={(e) => handleFieldChange('morningStiffness', e.target.value)}
                />
                <span>No</span>
              </label>
            </div>
          </div>

          {config.morningStiffnessDuration && formData.morningStiffness === 'Yes' && (
            <div className="field-group">
              <label className="field-label">
                How long does morning stiffness last?
              </label>
              <input
                type="text"
                value={formData.morningStiffnessDuration || ''}
                onChange={(e) => handleFieldChange('morningStiffnessDuration', e.target.value)}
                placeholder="e.g., 15 minutes, 1 hour, all day"
                className="form-input"
              />
            </div>
          )}

          <div className="field-group">
            <label className="field-label">
              Additional notes on stiffness or movement restrictions:
            </label>
            <textarea
              value={formData.stiffnessNotes || ''}
              onChange={(e) => handleFieldChange('stiffnessNotes', e.target.value)}
              placeholder="Any other relevant information about your movement restrictions..."
              rows={3}
              className="form-textarea"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StiffnessSection;
